import { supabase } from "@/integrations/supabase/client";

export interface NotificationData {
  store_id: string;
  user_id?: string;
  type: "booking" | "order" | "inventory" | "chat" | "staff";
  title: string;
  message: string;
  entity_type?: string;
  entity_id?: string;
  recipient_email: string;
  scheduled_for?: string;
  metadata?: Record<string, any>;
}

export interface NotificationTemplate {
  name: string;
  type: string;
  subject: string;
  html_template: string;
  variables: string[];
}

export interface NotificationPayload {
  to: string;
  type:
    | "welcome"
    | "invitation"
    | "password_reset"
    | "order_confirmation"
    | "login_notification"
    | "booking"
    | "inventory"
    | "chat"
    | "staff"
    | "order";
  data: {
    title?: string;
    body?: string;
    message_id?: string;
    chat_id?: string;
    sender_name?: string;
    sender_id?: string;
    store_id?: string;
    message_type?: string;
    [key: string]: any;
  };
}

/**
 * Send an email notification using the Supabase Edge Function
 * @param payload The notification payload
 * @returns Response with success status
 */
export const sendNotification = async (
  payload: NotificationPayload
): Promise<any> => {
  try {
    console.log("Sending notification:", payload);
    const { data, error } = await supabase.functions.invoke(
      "send-notification",
      {
        body: payload,
      }
    );

    if (error) {
      console.error("Error sending notification:", error);
      throw new Error(`Error sending notification: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error("Exception sending notification:", error);
    throw error;
  }
};

/**
 * Send a welcome email to a newly registered user
 * @param email User's email address
 * @param firstName User's first name
 * @param lastName User's last name
 * @returns Response from the notification service
 */
export const sendWelcomeEmail = async (
  email: string,
  firstName: string,
  lastName: string
): Promise<any> => {
  console.log(`Sending welcome email to ${firstName} ${lastName} at ${email}`);
  return sendNotification({
    to: email,
    type: "welcome",
    data: { firstName, lastName },
  });
};

/**
 * Send a login notification email
 * @param email User's email address
 * @param firstName User's first name
 * @returns Response from the notification service
 */
export const sendLoginNotificationEmail = async (
  email: string,
  firstName: string
): Promise<any> => {
  console.log(`Sending login notification to ${firstName} at ${email}`);
  return sendNotification({
    to: email,
    type: "login_notification",
    data: {
      firstName,
      loginTime: new Date().toLocaleString(),
      loginDate: new Date().toDateString(),
    },
  });
};

/**
 * Send an invitation email
 * @param email Recipient's email address
 * @param firstName Recipient's first name
 * @param storeName Name of the store
 * @param inviteUrl URL for accepting the invitation
 * @returns Response from the notification service
 */
export const sendInvitationEmail = async (
  email: string,
  firstName: string,
  storeName: string,
  inviteUrl: string
): Promise<any> => {
  return sendNotification({
    to: email,
    type: "invitation",
    data: { firstName, storeName, inviteUrl },
  });
};

/**
 * Send a password reset email
 * @param email User's email address
 * @param resetUrl URL for resetting the password
 * @returns Response from the notification service
 */
export const sendPasswordResetEmail = async (
  email: string,
  resetUrl: string
): Promise<any> => {
  return sendNotification({
    to: email,
    type: "password_reset",
    data: { resetUrl },
  });
};

/**
 * Send an order confirmation email
 * @param email Customer's email address
 * @param orderId ID of the order
 * @param items Order items
 * @param total Total amount of the order
 * @returns Response from the notification service
 */
export const sendOrderConfirmationEmail = async (
  email: string,
  orderId: string,
  items: any[],
  total: number
): Promise<any> => {
  return sendNotification({
    to: email,
    type: "order_confirmation",
    data: { orderId, items, total },
  });
};

export async function createNotification(data: NotificationData) {
  try {
    const { data: notification, error } = await supabase
      .from("notifications")
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return notification;
  } catch (error) {
    console.error("Error creating notification:", error);
    throw error;
  }
}

// Get notification templates
export async function getTemplate(
  templateName: string
): Promise<NotificationTemplate | null> {
  try {
    const { data, error } = await supabase
      .from("notification_templates")
      .select("*")
      .eq("name", templateName)
      .single();

    if (error) throw error;
    return {
      ...data,
      variables: Array.isArray(data.variables)
        ? data.variables
        : JSON.parse(data.variables as string),
    };
  } catch (error) {
    console.error("Error fetching template:", error);
    return null;
  }
}

// Helper methods for different notification types
export async function sendReservationConfirmation(reservationData: {
  customer_name: string;
  customer_email: string;
  store_name: string;
  store_id: string;
  reservation_date: string;
  reservation_time: string;
  party_size: number;
  table_name: string;
  booking_url: string;
}) {
  return sendNotification({
    to: reservationData.customer_email,
    type: "booking",
    data: reservationData,
  });
}

export async function sendReservationToStore(reservationData: {
  customer_name: string;
  customer_email: string;
  store_name: string;
  store_id: string;
  store_email: string;
  reservation_date: string;
  reservation_time: string;
  party_size: number;
  table_name: string;
  special_requests?: string;
  dashboard_url: string;
}) {
  console.log("Sending reservation to store....:", reservationData);
  return sendNotification({
    to: reservationData.store_email,
    type: "booking",
    data: reservationData,
  });
}

export async function sendOrderNotification(orderData: {
  order_id: string;
  store_name: string;
  store_id: string;
  store_email: string;
  table_name: string;
  table_number: number;
  total_amount: number;
  order_items: string;
  special_instructions?: string;
  dashboard_url: string;
  assigned_staff_email?: string;
}) {
  // Send to store
  await sendNotification({
    to: orderData.store_email,
    type: "order",
    data: orderData,
  });

  // Send to assigned staff if exists
  if (orderData.assigned_staff_email) {
    await sendNotification({
      to: orderData.assigned_staff_email,
      type: "order",
      data: {
        ...orderData,
        title: `New Order Assigned - ${orderData.table_name}`,
        message: `You have a new order assigned to your table`,
      },
    });
  }
}

export async function sendInventoryAlert(inventoryData: {
  item_name: string;
  current_quantity: number;
  minimum_quantity: number;
  unit_of_measure: string;
  store_name: string;
  store_id: string;
  store_email: string;
  inventory_url: string;
}) {
  return sendNotification({
    to: inventoryData.store_email,
    type: "inventory",
    data: inventoryData,
  });
}

export async function sendChatNotification(chatData: {
  sender_name: string;
  message_content: string;
  store_name: string;
  store_id: string;
  recipient_emails: string[];
  chat_url: string;
}) {
  return Promise.all(
    chatData.recipient_emails.map((email) =>
      sendNotification({
        to: email,
        type: "chat",
        data: chatData,
      })
    )
  );
}

export async function sendStaffNotification(staffData: {
  staff_name: string;
  attendance_type: string;
  details: string;
  store_name: string;
  store_id: string;
  manager_email: string;
  dashboard_url: string;
}) {
  return sendNotification({
    to: staffData.manager_email,
    type: "staff",
    data: staffData,
  });
}
